"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useCart } from "@/context/cart-context";
import { formatFourthwallMoney } from "@/lib/fourthwall";

export const CartDrawer = (): React.ReactElement => {
  const {
    cartItems,
    isCartOpen,
    isCheckingOut,
    checkoutError,
    cartCount,
    setCartOpen,
    updateQuantity,
    checkout,
  } = useCart();

  const drawerRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const drawer = drawerRef.current;

    if (!drawer) {
      return;
    }

    if (isCartOpen && !drawer.open) {
      drawer.showModal();
    } else if (!isCartOpen && drawer.open) {
      drawer.close();
    }
  }, [isCartOpen, setCartOpen]);

  // Calculate subtotal
  const subtotalValue = cartItems.reduce(
    (total, item) => total + item.variant.priceValue * item.quantity,
    0,
  );
  const currency = cartItems[0]?.variant.currency ?? "USD";
  const formattedSubtotal = formatFourthwallMoney({
    value: subtotalValue,
    currency,
  });

  return (
    <dialog
      ref={drawerRef}
      className="fixed inset-0 z-50 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent p-0 backdrop:bg-black/40 backdrop:backdrop-blur-xs"
      aria-labelledby="cart-title"
      onCancel={(event) => {
        event.preventDefault();
        setCartOpen(false);
      }}
      onClose={() => {
        if (isCartOpen) {
          setCartOpen(false);
        }
      }}
    >
      <div
        className="flex h-full w-full justify-end"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            setCartOpen(false);
          }
        }}
      >
        <div className="border-border relative flex h-full w-full max-w-md flex-col border-l bg-white shadow-2xl">
          {/* Header */}
          <div className="border-border flex items-center justify-between border-b px-4 py-5 sm:px-6">
            <div>
              <h2
                id="cart-title"
                className="font-sora text-foreground text-xl font-semibold"
              >
                Your cart
              </h2>
              <p className="text-secondary mt-1 text-xs">
                {cartCount} {cartCount === 1 ? "item" : "items"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="hover:text-primary text-foreground hover:bg-primary/5 flex h-11 w-11 items-center justify-center rounded-full transition-colors"
              aria-label="Close cart"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {cartItems.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
                  <ShoppingBag
                    className="text-primary h-8 w-8"
                    aria-hidden="true"
                  />
                </div>
                <p className="font-sora text-foreground mt-5 text-lg font-semibold">
                  Your cart is empty
                </p>
                <p className="text-secondary mt-2 max-w-xs text-sm leading-6">
                  Find something you like and it&apos;ll show up here.
                </p>
                <button
                  type="button"
                  onClick={() => setCartOpen(false)}
                  className="font-sora text-primary mt-6 text-sm font-semibold hover:underline"
                >
                  Keep browsing &rarr;
                </button>
              </div>
            ) : (
              <div className="space-y-4 pr-1">
                {cartItems.map((item) => {
                  const itemKey = `${item.productId}:${item.variant.id}`;

                  return (
                    <div
                      key={itemKey}
                      className="border-border hover:border-primary/30 grid grid-cols-[72px_minmax(0,1fr)] gap-3 rounded-2xl border bg-white p-3 shadow-xs transition-colors sm:grid-cols-[84px_minmax(0,1fr)] sm:gap-4 sm:p-4"
                    >
                      {/* Image container */}
                      <div className="bg-primary/5 relative aspect-square overflow-hidden rounded-xl">
                        {item.image ? (
                          <Image
                            src={item.image.transformedUrl ?? item.image.url}
                            alt={item.productName}
                            fill
                            sizes="84px"
                            className="object-cover"
                          />
                        ) : (
                          <div className="bg-primary/10 flex h-full w-full items-center justify-center">
                            <Image
                              src="/images/logos/logomark.png"
                              alt=""
                              width={36}
                              height={36}
                              className="opacity-80"
                            />
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex min-w-0 flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="font-sora text-foreground truncate text-sm font-semibold">
                              {item.productName}
                            </h3>
                            <button
                              type="button"
                              onClick={() => updateQuantity(itemKey, 0)}
                              className="text-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-red-50 hover:text-red-500"
                              aria-label={`Remove ${item.productName}`}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                          <p className="text-secondary mt-1 text-xs">
                            {[item.variant.colorName, item.variant.sizeName]
                              .filter(Boolean)
                              .join(" / ") || "One size"}
                          </p>
                        </div>

                        <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                          {/* Quantity picker */}
                          <div className="border-border inline-flex h-9 items-center rounded-lg border bg-white shadow-2xs">
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(itemKey, item.quantity - 1)
                              }
                              className="hover:text-primary text-foreground flex h-9 w-9 items-center justify-center transition-colors"
                              aria-label={`Decrease ${item.productName} quantity`}
                            >
                              <Minus className="h-3 w-3" aria-hidden="true" />
                            </button>
                            <span className="font-sora text-foreground min-w-6 text-center text-sm font-semibold">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(itemKey, item.quantity + 1)
                              }
                              className="hover:text-primary text-foreground flex h-9 w-9 items-center justify-center transition-colors"
                              aria-label={`Increase ${item.productName} quantity`}
                            >
                              <Plus className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </div>

                          {/* Price */}
                          {item.variant.priceLabel && (
                            <p className="font-sora text-primary text-sm font-semibold">
                              {item.variant.priceLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-border bg-primary/5 space-y-4 border-t p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <span className="font-sora text-secondary text-sm font-semibold">
                  Subtotal
                </span>
                <span className="font-sora text-foreground text-xl font-semibold">
                  {formattedSubtotal}
                </span>
              </div>

              <button
                type="button"
                disabled={isCheckingOut}
                onClick={checkout}
                className={`font-sora inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-semibold shadow-md transition-all duration-200 ${
                  isCheckingOut
                    ? "bg-muted text-muted-foreground cursor-not-allowed"
                    : "bg-primary hover:bg-primary/95 text-white hover:shadow-lg"
                }`}
              >
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                {isCheckingOut ? "Preparing checkout…" : "Checkout"}
              </button>

              {checkoutError && (
                <p className="text-center text-sm leading-5 font-medium text-red-600">
                  {checkoutError}
                </p>
              )}

              <p className="text-secondary text-center text-xs leading-5">
                Secure checkout powered by Fourthwall. Shipping, taxes, and
                discounts calculated at checkout.
              </p>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
};
